// Card PNGs are served S3 -> CloudFront -> Cloudflare -> browser. An interrupted
// download can be cached as if complete: the browser paints the scanlines it
// decoded, reports a full naturalWidth and fires onLoad (not onError), so no
// image-level signal reveals the damage. The only reliable tell is at the byte
// level — every valid PNG ends with the 8-byte IEND tail, and a partial file is
// missing it. These helpers detect that and heal a poisoned copy on the client.

// The fixed 8-byte trailer that closes every PNG stream (IEND chunk: length 0,
// type 'IEND', CRC). Its absence is the signature of a truncated download.
const IEND_TAIL = [0x49, 0x45, 0x4e, 0x44, 0xae, 0x42, 0x60, 0x82];

// true iff the final 8 bytes are the IEND tail. Accepts an ArrayBuffer or a
// Uint8Array; anything shorter than the tail itself is incomplete.
export const isCompletePng = (buffer) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes.length < IEND_TAIL.length) {
    return false;
  }
  const start = bytes.length - IEND_TAIL.length;
  return IEND_TAIL.every((byte, index) => bytes[start + index] === byte);
};

// A response is complete when the bytes end in IEND and, if the server declared
// a Content-Length, exactly that many bytes arrived (in-transit truncation can
// leave a plausible-looking tail but a short body).
export const isCompletePngResponse = ({ buffer, contentLength }) =>
  isCompletePng(buffer) && (contentLength == null || contentLength === buffer.byteLength);

// Fetch a PNG and report whether the delivered bytes form a complete file.
// fetchImpl is injectable for tests; contentLength is null when the header is
// absent (or zero) so the length check is skipped rather than tripped.
export const fetchPng = async (url, options, fetchImpl = fetch) => {
  const response = await fetchImpl(url, options);
  const buffer = await response.arrayBuffer();
  const contentLength = Number(response.headers.get('content-length')) || null;
  return {
    ok: response.ok,
    buffer,
    contentLength,
    complete: isCompletePngResponse({ buffer, contentLength })
  };
};

// A thrown fetch (network drop, CORS) is just another incomplete result — never
// let it crash the sweep that runs this across 52 cards.
const tryFetchPng = async (url, options, fetchImpl) => {
  try {
    return await fetchPng(url, options, fetchImpl);
  } catch (error) {
    return { complete: false, error };
  }
};

// Verify a card PNG and, if the browser is holding an incomplete copy, escalate:
//   1. plain fetch — if already complete, do nothing (no report).
//   2. {cache:'reload'} — bypass and repopulate the browser HTTP cache under the
//      canonical URL. This is the reported single-user case, fixed next app load.
//   3. ?heal=<token> {cache:'reload'} — a fresh cache key that routes around a
//      poisoned edge object, fetching straight from origin.
// Stages 2+ report via the injected `report` (Sentry in production) so a CDN
// regression during the Cloudflare migration is not silently masked. A canonical
// object poisoned at the edge can be detected and routed-around but not repaired
// on its canonical URL from the client — the 'edge'/'failed' report is the signal
// to run a CloudFront invalidation / Cloudflare purge.
export const healPngIfIncomplete = async (url, { fetchImpl = fetch, report = () => {}, cacheBust } = {}) => {
  const cached = await tryFetchPng(url, undefined, fetchImpl);
  if (cached.complete) {
    return { status: 'ok' };
  }

  const reloaded = await tryFetchPng(url, { cache: 'reload' }, fetchImpl);
  if (reloaded.complete) {
    const result = { url, status: 'healed', stage: 'browser-cache' };
    report(result);
    return result;
  }

  const token = cacheBust != null ? cacheBust : String(Date.now());
  const healedUrl = `${url}${url.includes('?') ? '&' : '?'}heal=${token}`;
  const busted = await tryFetchPng(healedUrl, { cache: 'reload' }, fetchImpl);
  if (busted.complete) {
    const result = { url, status: 'healed', stage: 'edge', healedUrl };
    report(result);
    return result;
  }

  const result = { url, status: 'failed', healedUrl };
  report(result);
  return result;
};

// Turn a heal result into what the on-screen <img> should do. A poisoned copy
// that already "loaded" never reloads on its own, so a healed card must be
// pointed at a src that forces a fresh fetch of the good bytes:
//   ok                     -> keep canonical; verified (cache is good)
//   healed (browser-cache) -> cache-bust canonical so the repopulated good bytes
//                             re-fetch (a stale in-memory decode may still linger);
//                             verified, since the canonical cache is now good
//   healed (edge)          -> the busted heal URL carries the good bytes, but the
//                             canonical object is still poisoned (needs a CDN purge),
//                             so NOT verified — a later mount must re-heal
//   failed                 -> keep canonical; caller shows the drawn placeholder
export const resolveHealedCard = (result, canonicalSrc, nonce) => {
  if (!result) {
    return { src: canonicalSrc, failed: false, verified: false };
  }
  if (result.status === 'ok') {
    return { src: canonicalSrc, failed: false, verified: true };
  }
  if (result.status === 'healed') {
    if (result.stage === 'edge') {
      return { src: result.healedUrl, failed: false, verified: false };
    }
    const separator = canonicalSrc.includes('?') ? '&' : '?';
    return { src: `${canonicalSrc}${separator}v=${nonce}`, failed: false, verified: true };
  }
  return { src: canonicalSrc, failed: true, verified: false };
};

const PLACEHOLDER_SUITS = { S: '♠', H: '♥', D: '♦', C: '♣' };
const PLACEHOLDER_RED = new Set(['H', 'D']);

// Build a self-contained inline-SVG data URI drawing a card face (rank + suit),
// or a plain back for a face-down card (no rank, so it can't leak identity).
// Used as an <img> src when an image can't be recovered, so the fallback is the
// SAME element type in the SAME box as a real card — the CardSuitGroup wrapper
// styles every <img> identically (border, rounding, sizing), so this never
// disturbs the hand layout. viewBox matches the real card aspect (357x500).
export const cardPlaceholderDataUri = (value, flipped) => {
  let svg;
  if (flipped) {
    svg =
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 357 500'>" +
      "<rect width='357' height='500' fill='#1e3a8a'/>" +
      "<rect x='20' y='20' width='317' height='460' fill='none' stroke='#2745a3' stroke-width='12'/>" +
      '</svg>';
  } else {
    const rank = value[0] === 'T' ? '10' : value[0];
    const colour = PLACEHOLDER_RED.has(value[1]) ? '#c62828' : '#212121';
    const suit = PLACEHOLDER_SUITS[value[1]] || '';
    svg =
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 357 500'>" +
      // Index top-left (rank over suit) so it stays visible in an overlapping
      // fanned hand, where only each card's left strip shows.
      "<rect width='357' height='500' fill='#ffffff'/>" +
      `<text x='52' y='96' text-anchor='middle' font-family='Helvetica,Arial,sans-serif' font-weight='bold' font-size='74' fill='${colour}'>${rank}</text>` +
      `<text x='52' y='172' text-anchor='middle' font-family='Helvetica,Arial,sans-serif' font-size='78' fill='${colour}'>${suit}</text>` +
      '</svg>';
  }
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};
